import { type OpenAPIV3_1 } from 'openapi-types';
import { prettifyCode } from '../../utils/prettifyCode';
import { extractProperties } from './extractProperties';
import { PrismCode } from '../PrismCode';
import { hasValue } from '../../utils/hasValue';

export const Example = ({
  input,
  title,
  withRadius = true
}: {
  input: OpenAPIV3_1.RequestBodyObject | OpenAPIV3_1.ResponseObject;
  title: string;
  withRadius?: boolean;
}) => {
  // If no content is present, return null
  if (!input?.content) {
    return (
      <PrismCode
        title={title}
        code={prettifyCode(input)}
        language="js"
        withRadius={withRadius}
      />
    );
  }

  // Extract the first content key and its corresponding value
  const contentType = Object.keys(input.content)[0];
  const firstValue = input.content?.[contentType];

  let sourceCode: string = '';

  // If the content type is 'application/json', extract properties
  if (contentType === 'application/json') {
    if (firstValue?.examples) {
      const stuff = Object.values(firstValue.examples)
        .map((example) => example as OpenAPIV3_1.ExampleObject)
        // ensures you get the value of the first ExampleObject that has a value property,
        // safely handling the case where no such example exists.
        .find((example) => example.value !== undefined)?.value;
      sourceCode = JSON.stringify(stuff, null, 2);
    } else {
      // this is the case when we don't have any examples defined in
      if (firstValue?.schema) {
        const extractedData = extractProperties(
          (firstValue.schema as OpenAPIV3_1.BaseSchemaObject)
            ?.properties as OpenAPIV3_1.SchemaObject
        );
        sourceCode = JSON.stringify(extractedData, null, 2);
      }
    }
  }

  // If the content is a text/plain, grab it differently and do not perform extractions
  if (contentType === 'text/plain' || contentType === 'text/csv') {
    if (hasValue(firstValue.examples)) {
      sourceCode = (firstValue?.examples?.[0] as OpenAPIV3_1.ExampleObject)
        ?.value;
    } else {
      sourceCode = (firstValue?.schema as OpenAPIV3_1.BaseSchemaObject)
        ?.example;
    }
  }

  if (!sourceCode) return null;

  return (
    <PrismCode
      title={title}
      code={prettifyCode(sourceCode)}
      language={contentType === 'application/json' ? 'js' : ''}
      withRadius={withRadius}
    />
  );
};
